import React from 'react';
import { MuiModal } from 'ui/MuiModal';
import { ContentModule } from 'components/content/models/ContentModule';
import { ConfirmDialog } from 'ui/dialogs/ConfirmDialog';
import { styled } from '@mui/material';
import { rulesApi } from 'components/rules/rulesApi';
import { RuleScheduledTaskNotificationStepper } from 'components/rules/scheduled-notification/modals/RuleScheduledTaskNotificationStepper';
import { CreateRuleResource, RuleResource } from 'components/rules/rules.types';
import { Location } from 'components/location/models/LocationModel';
import { Audience } from 'components/audience/models/segmentModel';
import { Perk } from 'components/loyalty/models/PerkModel';
import { AdjustmentScheme } from 'components/adjustment-schemes/adjustmentSchemeModel';

interface CreateRuleScheduledTaskNotificationModalProps {
    open: boolean;
    onClose?: () => void;
    onError: (message: string) => void;
    onSuccess: (rule: RuleResource) => void;
    rule?: RuleResource;
    modules: ContentModule[];
    locations: Array<Location>;
    audiences: Array<Audience>;
    perks: Array<Perk>;
    adjustmentSchemes: Array<AdjustmentScheme>;
}

const StyledMuiModal = styled(MuiModal)(() => ({
    overflow: 'visible' // Needed for the dropdowns
}));

export const CreateRuleScheduledTaskNotificationModal: React.FunctionComponent<
    CreateRuleScheduledTaskNotificationModalProps
> = props => {
    const { open, onClose } = props;
    return (
        <StyledMuiModal open={open} onClose={onClose}>
            <CreateRuleScheduledTaskNotificationModalComponent {...props} />
        </StyledMuiModal>
    );
};

const CreateRuleScheduledTaskNotificationModalComponent: React.FC<
    CreateRuleScheduledTaskNotificationModalProps
> = props => {
    const { onClose, onError, onSuccess, rule, modules, locations, perks, audiences, adjustmentSchemes } =
        props;

    const [openConfirm, setOpenConfirm] = React.useState(false);
    const onSubmit = React.useCallback(
        async (data: CreateRuleResource) => {
            const result = await rulesApi.create({
                body: data
            });

            if (!result.ok) {
                onError(result.body.message);
            } else {
                onSuccess(result.body);
            }
        },
        [onError, onSuccess]
    );
    const handleOnClose = React.useCallback(() => {
        setOpenConfirm(true);
    }, [setOpenConfirm]);
    const handleDialogOnCancel = React.useCallback(() => {
        setOpenConfirm(false);
    }, [setOpenConfirm]);

    return (
        <>
            <StyledMuiModal open={true} onClose={handleOnClose}>
                <RuleScheduledTaskNotificationStepper
                    initialData={rule}
                    modules={modules}
                    onClose={handleOnClose}
                    onSubmit={onSubmit}
                    locations={locations}
                    perks={perks}
                    adjustmentSchemes={adjustmentSchemes}
                    audiences={audiences}
                />
            </StyledMuiModal>
            <ConfirmDialog
                open={openConfirm}
                title="Hold On!"
                content="Are you sure you want to cancel this triggered notification creation process? All changes you made will be lost."
                onCancel={handleDialogOnCancel}
                onConfirm={onClose}
                cancelLabel={"No, don't cancel"}
                confirmLabel={'Yes, cancel'}
            />
        </>
    );
};
